import React, { useEffect, useState, useRef } from 'react';
import { signOut, onAuthStateChanged, User } from 'firebase/auth';
import { ref, onValue, set, remove, push, update, get } from 'firebase/database';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Student, Course, Exam, Announcement, Grade, ChatMessage } from '../types';

type Tab = 'dashboard' | 'courses' | 'exams' | 'grades' | 'announcements' | 'messages' | 'profile';

const SEMESTER = '2025-2026';

const tabLabels: { id: Tab; label: string }[] = [
  { id: 'dashboard', label: 'Ana Sayfa' },
  { id: 'courses', label: 'Ders İşlemleri' },
  { id: 'exams', label: 'Sınav İşlemleri' },
  { id: 'grades', label: 'Not Durumu' },
  { id: 'announcements', label: 'Duyurular' },
  { id: 'messages', label: 'Mesajlar' },
  { id: 'profile', label: 'Kullanıcı İşlemleri' },
];

const examTypeLabels: Record<string, string> = {
  midterm: 'Vize', final: 'Final', quiz: 'Quiz', makeup: 'Bütünleme',
};

const degreeLabels: Record<string, string> = {
  bachelor: 'Lisans', master: 'Yüksek Lisans', doctorate: 'Doktora',
};

// ── Icons ──────────────────────────────────────────────────────────────────────
const IconBook = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/></svg>
);
const IconExam = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-2 8H7v-2h4v2zm6 0h-4v-2h4v2zm0-4H7v-2h10v2z"/></svg>
);
const IconGrade = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>
);
const IconBell = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>
);
const IconMsg = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
);
const IconStar = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
);

// ── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard: React.FC<{ icon: React.ReactNode; title: string; value: number | string; color: string }> = ({ icon, title, value, color }) => (
  <div className="sp-stat" style={{ '--sp-color': color } as React.CSSProperties}>
    <div className="sp-stat-icon">{icon}</div>
    <div className="sp-stat-body">
      <div className="sp-stat-title">{title}</div>
      <div className="sp-stat-value">{value}</div>
    </div>
  </div>
);

// ── Dashboard Tab ──────────────────────────────────────────────────────────────
const DashboardTab: React.FC<{
  enrolledCount: number;
  upcomingExams: number;
  unreadAnnouncements: number;
  unreadMessages: number;
  gpa: string;
}> = ({ enrolledCount, upcomingExams, unreadAnnouncements, unreadMessages, gpa }) => (
  <>
    <div className="sp-page-header">
      <h2>Ana Sayfa</h2>
      <span className="sp-semester">{SEMESTER}</span>
    </div>
    <div className="sp-stats-grid">
      <StatCard icon={<IconBook />} title="Kayıtlı Dersler" value={enrolledCount} color="#1abc9c" />
      <StatCard icon={<IconExam />} title="Yaklaşan Sınavlar" value={upcomingExams} color="#f39c12" />
      <StatCard icon={<IconGrade />} title="GPA / Ortalama" value={gpa || '—'} color="#3498db" />
      <StatCard icon={<IconBell />} title="Yeni Duyurular" value={unreadAnnouncements} color="#9b59b6" />
      <StatCard icon={<IconMsg />} title="Okunmamış Mesaj" value={unreadMessages} color="#e74c3c" />
      <StatCard icon={<IconStar />} title="Aktif Dönem" value={SEMESTER} color="#e91e63" />
    </div>
    <div className="sp-section-grid">
      <div className="sp-card">
        <div className="sp-card-header">Hızlı Bilgi</div>
        <div className="sp-card-body">
          <div className="sp-quick-info">
            <div className="sp-qi-item"><span>Dönem</span><strong>{SEMESTER}</strong></div>
            <div className="sp-qi-item"><span>Kayıtlı Ders</span><strong>{enrolledCount} ders</strong></div>
            <div className="sp-qi-item"><span>Yaklaşan Sınav</span><strong>{upcomingExams} sınav</strong></div>
          </div>
        </div>
      </div>
      <div className="sp-card">
        <div className="sp-card-header">Bildirimler</div>
        <div className="sp-card-body">
          {unreadAnnouncements === 0 && unreadMessages === 0 ? (
            <p className="sp-empty">Yeni bildirim bulunmamaktadır.</p>
          ) : (
            <div className="sp-quick-info">
              {unreadAnnouncements > 0 && (
                <div className="sp-qi-item sp-qi-alert">
                  <IconBell /><span>{unreadAnnouncements} yeni duyuru var</span>
                </div>
              )}
              {unreadMessages > 0 && (
                <div className="sp-qi-item sp-qi-alert">
                  <IconMsg /><span>{unreadMessages} okunmamış mesaj var</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  </>
);

// ── Courses Tab ────────────────────────────────────────────────────────────────
const CoursesTab: React.FC<{ uid: string }> = ({ uid }) => {
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [enrolledIds, setEnrolledIds] = useState<Set<string>>(new Set());
  const [activeView, setActiveView] = useState<'enrolled' | 'all'>('enrolled');
  const [loading, setLoading] = useState(false);
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);
  const [courseLinks, setCourseLinks] = useState<Record<string, any[]>>({});

  useEffect(() => {
    const u1 = onValue(ref(db, 'courses'), snap => {
      const d = snap.val();
      const list = d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Course) })) : [];
      setAllCourses(list);
      list.forEach((c: Course) => {
        if (!c.id) return;
        onValue(ref(db, `courseLinks/${c.id}`), s => {
          const ld = s.val();
          setCourseLinks(prev => ({ ...prev, [c.id!]: ld ? Object.values(ld) : [] }));
        });
      });
    });
    const u2 = onValue(ref(db, `enrollments/${uid}`), snap => {
      const d = snap.val();
      setEnrolledIds(new Set(d ? Object.keys(d) : []));
    });
    return () => { u1(); u2(); };
  }, [uid]);

  const enroll = async (courseId: string) => {
    setLoading(true);
    await set(ref(db, `enrollments/${uid}/${courseId}`), true);
    setLoading(false);
  };

  const drop = async (courseId: string) => {
    if (!window.confirm('Bu dersten kaydınızı silmek istediğinizden emin misiniz?')) return;
    setLoading(true);
    await remove(ref(db, `enrollments/${uid}/${courseId}`));
    setLoading(false);
  };

  const isYouTube = (url: string) => url.includes('youtube.com') || url.includes('youtu.be');

  const enrolledCourses = allCourses.filter(c => c.id && enrolledIds.has(c.id));
  const availableCourses = allCourses.filter(c => c.id && !enrolledIds.has(c.id));

  const MaterialsRow: React.FC<{ course: Course }> = ({ course }) => {
    const links = courseLinks[course.id!] || [];
    if (links.length === 0) return <p className="sp-empty" style={{ padding: '10px 0' }}>Bu ders için materyal eklenmemiş.</p>;
    return (
      <div className="sp-materials">
        {links.map((l: any, i: number) => (
          <a key={i} href={l.url} target="_blank" rel="noreferrer" className={`sp-material-item ${isYouTube(l.url) ? 'sp-material-link' : ''}`}>
            <span>{isYouTube(l.url) ? '▶️' : '🔗'}</span> {l.title}
          </a>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="sp-page-header">
        <h2>Ders İşlemleri</h2>
        <span className="sp-semester">{SEMESTER}</span>
      </div>

      <div className="sp-tab-toggle">
        <button className={activeView === 'enrolled' ? 'active' : ''} onClick={() => setActiveView('enrolled')}>
          Kayıtlı Derslerim ({enrolledCourses.length})
        </button>
        <button className={activeView === 'all' ? 'active' : ''} onClick={() => setActiveView('all')}>
          Tüm Dersler ({availableCourses.length})
        </button>
      </div>

      {activeView === 'enrolled' && (
        <div className="sp-card">
          <div className="sp-card-header">Kayıtlı Derslerim</div>
          <div className="sp-card-body sp-table-body">
            {enrolledCourses.length === 0 ? (
              <p className="sp-empty">Henüz hiçbir derse kayıt olmadınız. "Tüm Dersler" sekmesinden ders ekleyebilirsiniz.</p>
            ) : (
              <table className="sp-table">
                <thead>
                  <tr><th>Kod</th><th>Ders Adı</th><th>Kredi</th><th>Öğretim Üyesi</th><th>Program</th><th>Materyal</th><th></th></tr>
                </thead>
                <tbody>
                  {enrolledCourses.map(c => (
                    <React.Fragment key={c.id}>
                      <tr>
                        <td className="sp-code">{c.code}</td>
                        <td className="sp-name">{c.name}</td>
                        <td>{c.credits}</td>
                        <td>{c.instructor}</td>
                        <td>{c.schedule}</td>
                        <td>
                          <button className="sp-btn sp-btn-material" onClick={() => setExpandedCourseId(expandedCourseId === c.id ? null : c.id!)}>
                            {courseLinks[c.id!]?.length || 0} materyal
                          </button>
                        </td>
                        <td>
                          <button className="sp-btn sp-btn-drop" onClick={() => drop(c.id!)} disabled={loading}>
                            Sil
                          </button>
                        </td>
                      </tr>
                      {expandedCourseId === c.id && (
                        <tr>
                          <td colSpan={7} style={{ background: '#f8f9fa', padding: '12px 16px' }}>
                            <MaterialsRow course={c} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeView === 'all' && (
        <div className="sp-card">
          <div className="sp-card-header">Mevcut Dersler</div>
          <div className="sp-card-body sp-table-body">
            {availableCourses.length === 0 ? (
              <p className="sp-empty">
                {allCourses.length === 0 ? 'Henüz ders eklenmemiş.' : 'Tüm derslere kayıt oldunuz.'}
              </p>
            ) : (
              <table className="sp-table">
                <thead>
                  <tr><th>Kod</th><th>Ders Adı</th><th>Kredi</th><th>Öğretim Üyesi</th><th>Program</th><th></th></tr>
                </thead>
                <tbody>
                  {availableCourses.map(c => (
                    <tr key={c.id}>
                      <td className="sp-code">{c.code}</td>
                      <td className="sp-name">{c.name}</td>
                      <td>{c.credits}</td>
                      <td>{c.instructor}</td>
                      <td>{c.schedule}</td>
                      <td>
                        <button className="sp-btn sp-btn-enroll" onClick={() => enroll(c.id!)} disabled={loading}>
                          Kayıt Ol
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ── Exams Tab ──────────────────────────────────────────────────────────────────
const ExamsTab: React.FC<{ uid: string }> = ({ uid }) => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [enrolledIds, setEnrolledIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const u1 = onValue(ref(db, 'exams'), snap => {
      const d = snap.val();
      setExams(d
        ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Exam) })).sort((a, b) => a.date.localeCompare(b.date))
        : []);
    });
    const u2 = onValue(ref(db, `enrollments/${uid}`), snap => {
      const d = snap.val();
      setEnrolledIds(new Set(d ? Object.keys(d) : []));
    });
    return () => { u1(); u2(); };
  }, [uid]);

  const today = new Date().toISOString().split('T')[0];
  const myExams = exams.filter(ex => enrolledIds.has(ex.courseId));
  const upcoming = myExams.filter(ex => ex.date >= today);
  const past = myExams.filter(ex => ex.date < today);

  const ExamRow: React.FC<{ ex: Exam; isPast?: boolean }> = ({ ex, isPast }) => (
    <tr className={isPast ? 'sp-row-past' : ''}>
      <td className="sp-code">{ex.courseCode}</td>
      <td className="sp-name">{ex.courseName}</td>
      <td><span className={`sp-exam-badge sp-exam-${ex.type}`}>{examTypeLabels[ex.type]}</span></td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {new Date(ex.date + 'T00:00:00').toLocaleDateString('tr-TR', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' })}
      </td>
      <td>{ex.time}</td>
      <td>{ex.location}</td>
    </tr>
  );

  return (
    <>
      <div className="sp-page-header">
        <h2>Sınav İşlemleri</h2>
        <span className="sp-semester">{SEMESTER}</span>
      </div>

      <div className="sp-card" style={{ marginBottom: 16 }}>
        <div className="sp-card-header">Yaklaşan Sınavlar</div>
        <div className="sp-card-body sp-table-body">
          {upcoming.length === 0 ? (
            <p className="sp-empty">Yaklaşan sınav bulunmamaktadır.</p>
          ) : (
            <table className="sp-table">
              <thead>
                <tr><th>Kod</th><th>Ders</th><th>Tür</th><th>Tarih</th><th>Saat</th><th>Yer</th></tr>
              </thead>
              <tbody>{upcoming.map(ex => <ExamRow key={ex.id} ex={ex} />)}</tbody>
            </table>
          )}
        </div>
      </div>

      {past.length > 0 && (
        <div className="sp-card">
          <div className="sp-card-header">Geçmiş Sınavlar</div>
          <div className="sp-card-body sp-table-body">
            <table className="sp-table">
              <thead>
                <tr><th>Kod</th><th>Ders</th><th>Tür</th><th>Tarih</th><th>Saat</th><th>Yer</th></tr>
              </thead>
              <tbody>{past.map(ex => <ExamRow key={ex.id} ex={ex} isPast />)}</tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

// ── Grades Tab ────────────────────────────────────────────────────────────────
const gpaMap: Record<string, number> = {
  AA: 4.0, BA: 3.5, BB: 3.0, CB: 2.5, CC: 2.0, DC: 1.5, DD: 1.0, FF: 0.0, P: 4.0, F: 0.0, I: 0.0, W: 0.0,
};

const GradesTab: React.FC<{ uid: string }> = ({ uid }) => {
  const [grades, setGrades] = useState<Grade[]>([]);

  useEffect(() => {
    return onValue(ref(db, `grades/${uid}`), snap => {
      const d = snap.val();
      setGrades(d ? Object.values(d) as Grade[] : []);
    });
  }, [uid]);

  const totalCredits = grades.filter(g => g.letterGrade && gpaMap[g.letterGrade] !== undefined).reduce((s, g) => s + g.credits, 0);
  const gpaSum = grades.filter(g => g.letterGrade && gpaMap[g.letterGrade] !== undefined).reduce((s, g) => s + gpaMap[g.letterGrade!] * g.credits, 0);
  const gpa = totalCredits > 0 ? (gpaSum / totalCredits).toFixed(2) : null;

  return (
    <>
      <div className="sp-page-header">
        <h2>Not Durumu</h2>
        <span className="sp-semester">{SEMESTER}</span>
      </div>

      {gpa && (
        <div className="sp-gpa-banner">
          <div className="sp-gpa-label">Genel Ortalama (GPA)</div>
          <div className="sp-gpa-value">{gpa}</div>
          <div className="sp-gpa-credits">{totalCredits} kredi tamamlandı</div>
        </div>
      )}

      <div className="sp-card">
        <div className="sp-card-header">Transkript</div>
        <div className="sp-card-body sp-table-body">
          {grades.length === 0 ? (
            <p className="sp-empty">Henüz not girilmemiş.</p>
          ) : (
            <table className="sp-table">
              <thead>
                <tr><th>Kod</th><th>Ders Adı</th><th>Kredi</th><th>Dönem</th><th>Vize</th><th>Final</th><th>Harf Notu</th></tr>
              </thead>
              <tbody>
                {grades.map(g => (
                  <tr key={g.courseId}>
                    <td className="sp-code">{g.courseCode}</td>
                    <td className="sp-name">{g.courseName}</td>
                    <td>{g.credits}</td>
                    <td>{g.semester}</td>
                    <td>{g.midterm !== undefined ? g.midterm : '—'}</td>
                    <td>{g.final !== undefined ? g.final : '—'}</td>
                    <td>
                      {g.letterGrade
                        ? <span className={`sp-letter-grade sp-grade-${g.letterGrade === 'FF' || g.letterGrade === 'F' ? 'fail' : 'pass'}`}>{g.letterGrade}</span>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

// ── Announcements Tab ──────────────────────────────────────────────────────────
const AnnouncementsTab: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  useEffect(() => {
    return onValue(ref(db, 'announcements'), snap => {
      const d = snap.val();
      setAnnouncements(d
        ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Announcement) })).sort((a, b) => b.createdAt - a.createdAt)
        : []);
    });
  }, []);

  const fmt = (ts: number) =>
    new Date(ts).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <div className="sp-page-header">
        <h2>Duyurular</h2>
        <span className="sp-semester">{SEMESTER}</span>
      </div>

      {announcements.length === 0 ? (
        <div className="sp-card">
          <div className="sp-card-body"><p className="sp-empty">Duyuru bulunmamaktadır.</p></div>
        </div>
      ) : announcements.map(a => (
        <div className={`sp-ann-card ${a.important ? 'sp-ann-important' : ''}`} key={a.id}>
          <div className="sp-ann-top">
            {a.important && <span className="sp-ann-badge">Önemli</span>}
            <div className="sp-ann-title">{a.title}</div>
            <div className="sp-ann-date">{fmt(a.createdAt)}</div>
          </div>
          <div className="sp-ann-body">{a.body}</div>
        </div>
      ))}
    </>
  );
};

// ── Messages Tab ───────────────────────────────────────────────────────────────
const MessagesTab: React.FC<{ uid: string; studentName: string; studentEmail: string }> = ({ uid, studentName, studentEmail }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsub = onValue(ref(db, `messages/${uid}`), snap => {
      const d = snap.val();
      const msgs: ChatMessage[] = d
        ? Object.entries(d).map(([id, v]) => ({ id, ...(v as ChatMessage) })).sort((a, b) => a.createdAt - b.createdAt)
        : [];
      setMessages(msgs);

      // Mark admin messages as read
      msgs.filter(m => m.from === 'admin' && !m.read).forEach(m => {
        update(ref(db, `messages/${uid}/${m.id}`), { read: true });
      });
    });
    return unsub;
  }, [uid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);

    const newMsg: Omit<ChatMessage, 'id'> = {
      from: 'student',
      text: text.trim(),
      createdAt: Date.now(),
      read: false,
    };

    const threadRef = ref(db, `messageThreads/${uid}`);
    const threadSnap = await get(threadRef);

    await push(ref(db, `messages/${uid}`), newMsg);
    await set(threadRef, {
      ...(threadSnap.val() || {}),
      uid,
      studentName,
      studentEmail,
      lastMessage: newMsg.text,
      lastAt: newMsg.createdAt,
      unreadByAdmin: true,
    });

    setText('');
    setSending(false);
  };

  const fmt = (ts: number) =>
    new Date(ts).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) +
    ' · ' +
    new Date(ts).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });

  return (
    <>
      <div className="sp-page-header">
        <h2>Mesajlar</h2>
        <span className="sp-semester">Danışman ile iletişim</span>
      </div>

      <div className="sp-chat-wrap">
        <div className="sp-chat-messages">
          {messages.length === 0 && (
            <div className="sp-chat-empty">
              <IconMsg />
              <p>Danışmanınıza mesaj göndermek için aşağıdaki formu kullanın.</p>
            </div>
          )}
          {messages.map(m => (
            <div key={m.id} className={`sp-bubble-wrap ${m.from === 'student' ? 'sp-bubble-right' : 'sp-bubble-left'}`}>
              <div className={`sp-bubble ${m.from === 'student' ? 'sp-bubble-student' : 'sp-bubble-admin'}`}>
                <p>{m.text}</p>
                <span className="sp-bubble-time">{fmt(m.createdAt)}</span>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <form onSubmit={handleSend} className="sp-chat-form">
          <input
            className="sp-chat-input"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Mesajınızı yazın..."
            disabled={sending}
          />
          <button type="submit" className="sp-chat-send" disabled={sending || !text.trim()}>
            {sending ? '...' : 'Gönder'}
          </button>
        </form>
      </div>
    </>
  );
};

// ── Profile Tab ────────────────────────────────────────────────────────────────
const ProfileTab: React.FC<{ student: Student | null }> = ({ student }) => (
  <>
    <div className="sp-page-header"><h2>Kullanıcı İşlemleri</h2></div>
    <div className="sp-card">
      <div className="sp-card-header">Öğrenci Bilgileri</div>
      <div className="sp-card-body sp-profile-body">
        {student ? (
          <div className="sp-profile-grid">
            <div className="sp-profile-field"><label>Ad Soyad</label><p>{student.fullName}</p></div>
            <div className="sp-profile-field"><label>Öğrenci No</label><p>{student.studentId}</p></div>
            <div className="sp-profile-field"><label>E-posta</label><p>{student.email}</p></div>
            <div className="sp-profile-field"><label>Program</label><p>{student.program}</p></div>
            <div className="sp-profile-field"><label>Öğrenim Düzeyi</label>
              <p>{degreeLabels[student.degree] || student.degree}</p>
            </div>
            <div className="sp-profile-field"><label>Kayıt Tarihi</label>
              <p>{new Date(student.createdAt).toLocaleDateString('tr-TR')}</p>
            </div>
          </div>
        ) : (
          <p className="sp-empty">Bilgiler yükleniyor...</p>
        )}
      </div>
    </div>
  </>
);

// ── Main Portal ────────────────────────────────────────────────────────────────
const StudentPortal: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [tab, setTab] = useState<Tab>('dashboard');
  const [authLoading, setAuthLoading] = useState(true);

  // Dashboard stats
  const [enrolledIds, setEnrolledIds] = useState<Set<string>>(new Set());
  const [exams, setExams] = useState<Exam[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      setAuthLoading(false);
      if (!u) navigate('/student/login');
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const u1 = onValue(ref(db, `students/${user.uid}`), snap => {
      if (snap.exists()) setStudent(snap.val() as Student);
    });
    const u2 = onValue(ref(db, `enrollments/${user.uid}`), snap => {
      const d = snap.val();
      setEnrolledIds(new Set(d ? Object.keys(d) : []));
    });
    const u3 = onValue(ref(db, 'exams'), snap => {
      const d = snap.val();
      setExams(d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Exam) })) : []);
    });
    const u4 = onValue(ref(db, 'announcements'), snap => {
      const d = snap.val();
      setAnnouncements(d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Announcement) })) : []);
    });
    const u5 = onValue(ref(db, `messages/${user.uid}`), snap => {
      const d = snap.val();
      setMessages(d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as ChatMessage) })) : []);
    });
    const u6 = onValue(ref(db, `grades/${user.uid}`), snap => {
      const d = snap.val();
      setGrades(d ? Object.values(d) as Grade[] : []);
    });
    return () => { u1(); u2(); u3(); u4(); u5(); u6(); };
  }, [user]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/student/login');
  };

  if (authLoading) {
    return (
      <div className="sp-loading">
        <div className="sp-spinner" />
      </div>
    );
  }

  if (!user) return null;

  const today = new Date().toISOString().split('T')[0];
  const upcomingExams = exams.filter(ex => enrolledIds.has(ex.courseId) && ex.date >= today).length;
  const unreadMessages = messages.filter(m => m.from === 'admin' && !m.read).length;
  const totalCredits = grades.filter(g => g.letterGrade && gpaMap[g.letterGrade] !== undefined).reduce((s, g) => s + g.credits, 0);
  const gpaSum = grades.filter(g => g.letterGrade && gpaMap[g.letterGrade] !== undefined).reduce((s, g) => s + gpaMap[g.letterGrade!] * g.credits, 0);
  const gpa = totalCredits > 0 ? (gpaSum / totalCredits).toFixed(2) : '';

  const displayName = student?.fullName || user.displayName || user.email || 'Öğrenci';

  const navTabLabels = tabLabels.map(t => {
    if (t.id === 'messages' && unreadMessages > 0) return { ...t, label: `Mesajlar (${unreadMessages})` };
    if (t.id === 'courses') return { ...t, label: `Dersler (${enrolledIds.size})` };
    return t;
  });

  return (
    <div className="sp-layout">
      <aside className="sp-sidebar">
        <div className="sp-sidebar-top">
          <div className="sp-sidebar-seal">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2" fill="none"/>
              <path d="M24 8L26.5 15.5H34.5L28 20L30.5 27.5L24 23L17.5 27.5L20 20L13.5 15.5H21.5L24 8Z" fill="currentColor"/>
              <circle cx="24" cy="32" r="4" fill="currentColor"/>
            </svg>
          </div>
          <div className="sp-sidebar-univ">University of American Atlantic</div>
          <div className="sp-sidebar-sub">Öğrenci Bilgi Sistemi</div>
        </div>

        <nav className="sp-nav">
          {navTabLabels.map(t => (
            <button
              key={t.id}
              className={`sp-nav-item ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <span className="sp-nav-arrow">›</span>
              {t.label}
            </button>
          ))}
        </nav>

        <div className="sp-sidebar-foot">
          <button className="sp-logout" onClick={handleLogout}>Çıkış Yap</button>
        </div>
      </aside>

      <div className="sp-content-wrap">
        <header className="sp-topbar">
          <div className="sp-topbar-left">
            <span className="sp-topbar-semester">{SEMESTER}</span>
          </div>
          <div className="sp-topbar-right">
            <div className="sp-user-badge">
              <div className="sp-user-avatar">{displayName.charAt(0).toUpperCase()}</div>
              <span className="sp-user-name">{displayName}</span>
            </div>
          </div>
        </header>

        <main className="sp-main">
          {tab === 'dashboard' && (
            <DashboardTab
              enrolledCount={enrolledIds.size}
              upcomingExams={upcomingExams}
              unreadAnnouncements={announcements.length}
              unreadMessages={unreadMessages}
              gpa={gpa}
            />
          )}
          {tab === 'courses' && <CoursesTab uid={user.uid} />}
          {tab === 'exams' && <ExamsTab uid={user.uid} />}
          {tab === 'grades' && <GradesTab uid={user.uid} />}
          {tab === 'announcements' && <AnnouncementsTab />}
          {tab === 'messages' && (
            <MessagesTab
              uid={user.uid}
              studentName={student?.fullName || displayName}
              studentEmail={student?.email || user.email || ''}
            />
          )}
          {tab === 'profile' && <ProfileTab student={student} />}
        </main>
      </div>
    </div>
  );
};

export default StudentPortal;
